import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
  AlertDialogTrigger,
} from '../components/ui/alert-dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import {
  ArrowLeft, Plus, Trash2, Sparkles, Eye, Video, Tag as TagIcon, X,
  BarChart3, Check, Image as ImageIcon,
} from 'lucide-react';
import { TipTapEditor } from '../components/TipTapEditor';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const EMPTY_FORM = {
  title: '', subtitle: '', cover_image_url: '', body_html: '',
  videos: [], tags: [], all_tenants: true, target_tenants: [],
  target_plans: [], target_status: [], is_published: false,
};

// ------- Signed upload helper (Cloudinary) -------
async function cloudinaryUpload(file, folder, resourceType = 'image') {
  const sig = await axios.get(
    `${BACKEND_URL}/api/cloudinary/signature?folder=${encodeURIComponent(folder)}&resource_type=${resourceType}`,
    { withCredentials: true }
  );
  const fd = new FormData();
  fd.append('file', file);
  fd.append('api_key', sig.data.api_key);
  fd.append('timestamp', sig.data.timestamp);
  fd.append('signature', sig.data.signature);
  fd.append('folder', sig.data.folder);
  const endpoint = `https://api.cloudinary.com/v1_1/${sig.data.cloud_name}/${resourceType}/upload`;
  const up = await axios.post(endpoint, fd);
  return up.data.secure_url;
}

// ------- Cover image upload block -------
const CoverUpload = ({ value, onChange }) => {
  const [uploading, setUploading] = useState(false);
  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const url = await cloudinaryUpload(file, 'meadd/insights/covers', 'image');
      onChange(url);
      toast.success('Capa carregada!');
    } catch (_) { toast.error('Falha ao enviar capa.'); }
    finally { setUploading(false); }
  };
  return (
    <div>
      <Label className="text-sm font-semibold">Capa (1200×628, opcional)</Label>
      <div className="mt-1.5 space-y-2">
        {value ? (
          <div className="relative">
            <img src={value} alt="Capa do artigo" className="w-full aspect-[1200/628] object-cover rounded-lg border border-slate-200" />
            <button type="button" onClick={() => onChange('')} className="absolute top-2 right-2 rounded-full bg-white/90 p-1.5 shadow hover:bg-white">
              <Trash2 className="h-3.5 w-3.5 text-rose-600" />
            </button>
          </div>
        ) : (
          <label className="flex flex-col items-center justify-center gap-2 aspect-[1200/628] border-2 border-dashed border-slate-300 rounded-lg cursor-pointer hover:bg-slate-50">
            <ImageIcon className="h-8 w-8 text-slate-400" />
            <span className="text-xs text-slate-500">
              {uploading ? 'Enviando…' : 'Clique para enviar a capa'}
            </span>
            <input type="file" accept="image/*" className="hidden" onChange={handleFile} disabled={uploading} />
          </label>
        )}
      </div>
    </div>
  );
};

// ------- Videos block (URL + optional MP4 upload) -------
const VideosBlock = ({ videos, onChange }) => {
  const [uploading, setUploading] = useState(false);
  const detectProvider = (url) => {
    if (!url) return 'external';
    if (/youtu\.?be/.test(url)) return 'youtube';
    if (/vimeo\.com/.test(url)) return 'vimeo';
    if (/cloudinary\.com/.test(url)) return 'cloudinary';
    return 'external';
  };
  const addUrl = () => {
    const url = window.prompt('Cole a URL do vídeo (YouTube, Vimeo ou outro)');
    if (!url) return;
    onChange([...videos, { url, provider: detectProvider(url), title: '' }]);
  };
  const uploadMp4 = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const url = await cloudinaryUpload(file, 'meadd/insights/videos', 'video');
      onChange([...videos, { url, provider: 'cloudinary', title: file.name.replace(/\.[^.]+$/, '') }]);
      toast.success('Vídeo enviado!');
    } catch (_) { toast.error('Falha ao enviar vídeo.'); }
    finally { setUploading(false); e.target.value = ''; }
  };
  const removeAt = (i) => onChange(videos.filter((_, idx) => idx !== i));
  const updateTitle = (i, title) => onChange(videos.map((v, idx) => (idx === i ? { ...v, title } : v)));

  return (
    <div>
      <Label className="text-sm font-semibold">Vídeos</Label>
      <div className="mt-1.5 space-y-2">
        {videos.map((v, i) => (
          <div key={i} className="flex items-center gap-2 border border-slate-200 rounded-lg p-2 bg-white">
            <Badge variant="outline" className="uppercase text-[9px] font-bold">
              {v.provider}
            </Badge>
            <Input
              value={v.title || ''}
              onChange={(e) => updateTitle(i, e.target.value)}
              placeholder="Título do vídeo (opcional)"
              className="h-8 flex-1 text-xs"
            />
            <a href={v.url} target="_blank" rel="noreferrer" className="text-xs text-emerald-700 hover:underline truncate max-w-[240px]">
              {v.url}
            </a>
            <button type="button" onClick={() => removeAt(i)} className="p-1 text-rose-600 hover:bg-rose-50 rounded">
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        ))}
        <div className="flex flex-wrap gap-2">
          <Button type="button" variant="outline" size="sm" onClick={addUrl} className="gap-1.5">
            <Video className="h-3.5 w-3.5" /> Adicionar URL (YouTube/Vimeo)
          </Button>
          <label className="inline-flex">
            <Button type="button" variant="outline" size="sm" asChild disabled={uploading} className="gap-1.5 cursor-pointer">
              <span>
                <Video className="h-3.5 w-3.5" /> {uploading ? 'Enviando…' : 'Upload MP4'}
                <input type="file" accept="video/*" className="hidden" onChange={uploadMp4} disabled={uploading} />
              </span>
            </Button>
          </label>
        </div>
      </div>
    </div>
  );
};

// ------- Tags block with autocomplete -------
const TagsBlock = ({ tags, onChange }) => {
  const [inputVal, setInputVal] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  useEffect(() => {
    if (!inputVal.trim()) { setSuggestions([]); return; }
    const t = setTimeout(async () => {
      try {
        const r = await axios.get(
          `${BACKEND_URL}/api/superadmin/insights/tags/suggest?q=${encodeURIComponent(inputVal)}`,
          { withCredentials: true }
        );
        setSuggestions((r.data || []).filter((s) => !tags.includes(s.tag)));
      } catch (_) { /* silent */ }
    }, 250);
    return () => clearTimeout(t);
  }, [inputVal, tags]);
  const addTag = (raw) => {
    const t = (raw || '').trim().toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9\-]/g, '');
    if (!t || tags.includes(t)) return;
    onChange([...tags, t]);
    setInputVal('');
    setSuggestions([]);
  };
  return (
    <div>
      <Label className="text-sm font-semibold">Tags</Label>
      <div className="mt-1.5 border border-slate-200 rounded-lg p-2 bg-white">
        <div className="flex flex-wrap gap-1.5 mb-1.5">
          {tags.map((t) => (
            <Badge key={t} variant="secondary" className="gap-1 pr-1">
              <TagIcon className="h-3 w-3" />
              {t}
              <button type="button" onClick={() => onChange(tags.filter((x) => x !== t))} className="ml-0.5 p-0.5 hover:bg-slate-200 rounded">
                <X className="h-2.5 w-2.5" />
              </button>
            </Badge>
          ))}
        </div>
        <Input
          value={inputVal}
          onChange={(e) => setInputVal(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); addTag(inputVal); } }}
          placeholder="Digite e pressione Enter para adicionar"
          className="h-8 text-xs border-0 focus-visible:ring-0"
          data-testid="insight-tags-input"
        />
        {suggestions.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-2 pt-2 border-t border-slate-100">
            {suggestions.map((s) => (
              <button key={s.tag} type="button" onClick={() => addTag(s.tag)}
                className="text-[10px] px-2 py-0.5 rounded-full border border-slate-200 hover:bg-slate-100">
                {s.tag} <span className="text-slate-400">({s.count})</span>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// ------- Targeting block -------
const TargetingBlock = ({ form, setForm, allTenants }) => (
  <div className="space-y-3">
    <Label className="text-sm font-semibold">Quem deve receber?</Label>
    <label className="flex items-center gap-2 cursor-pointer">
      <input
        type="checkbox"
        checked={form.all_tenants}
        onChange={(e) => setForm({ ...form, all_tenants: e.target.checked })}
        className="h-4 w-4 rounded border-slate-300 text-emerald-600"
        data-testid="insight-all-tenants"
      />
      <span className="text-sm">📢 Todos os clientes ativos</span>
    </label>
    {!form.all_tenants && (
      <div className="pl-6 space-y-3 border-l-2 border-slate-100">
        <div>
          <Label className="text-xs font-semibold text-slate-600 uppercase">Clientes específicos</Label>
          <div className="mt-1 border border-slate-200 rounded-lg p-2 bg-white max-h-40 overflow-y-auto">
            {allTenants.length === 0 && <p className="text-xs text-slate-400 p-2">Sem clientes cadastrados</p>}
            {allTenants.map((t) => (
              <label key={t.id} className="flex items-center gap-2 py-0.5 cursor-pointer text-xs hover:bg-slate-50 rounded px-1">
                <input
                  type="checkbox"
                  checked={form.target_tenants.includes(t.id)}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      target_tenants: e.target.checked
                        ? [...form.target_tenants, t.id]
                        : form.target_tenants.filter((id) => id !== t.id),
                    })
                  }
                  className="h-3.5 w-3.5 rounded border-slate-300 text-emerald-600"
                />
                <span className="flex-1">{t.name}</span>
                <span className="text-[10px] text-slate-400 uppercase">{t.plan || 'standard'}</span>
              </label>
            ))}
          </div>
        </div>
        <div>
          <Label className="text-xs font-semibold text-slate-600 uppercase">Por plano</Label>
          <div className="mt-1 flex flex-wrap gap-2">
            {['standard', 'pro', 'enterprise'].map((p) => (
              <label key={p} className="inline-flex items-center gap-1.5 text-xs cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.target_plans.includes(p)}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      target_plans: e.target.checked
                        ? [...form.target_plans, p]
                        : form.target_plans.filter((x) => x !== p),
                    })
                  }
                  className="h-3.5 w-3.5 rounded"
                />
                {p}
              </label>
            ))}
          </div>
        </div>
        <div>
          <Label className="text-xs font-semibold text-slate-600 uppercase">Por status</Label>
          <div className="mt-1 flex flex-wrap gap-2">
            {['active', 'paused', 'canceled'].map((s) => (
              <label key={s} className="inline-flex items-center gap-1.5 text-xs cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.target_status.includes(s)}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      target_status: e.target.checked
                        ? [...form.target_status, s]
                        : form.target_status.filter((x) => x !== s),
                    })
                  }
                  className="h-3.5 w-3.5 rounded"
                />
                {s}
              </label>
            ))}
          </div>
        </div>
      </div>
    )}
  </div>
);

// ------- Analytics dialog -------
const AnalyticsDialog = ({ insight, open, onOpenChange }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (!open || !insight?.id) return;
    setLoading(true);
    axios.get(`${BACKEND_URL}/api/superadmin/insights/${insight.id}/analytics`, { withCredentials: true })
      .then((r) => setData(r.data))
      .catch(() => toast.error('Falha ao carregar analytics'))
      .finally(() => setLoading(false));
  }, [open, insight]);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="insight-analytics-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <BarChart3 className="h-5 w-5 text-emerald-600" />
            Analytics — {insight?.title}
          </DialogTitle>
          <DialogDescription>Somente super admin visualiza. Views registrados por leitura.</DialogDescription>
        </DialogHeader>
        {loading ? <div className="py-8 text-center text-sm text-slate-500">Carregando…</div> : data && (
          <>
            <div className="grid grid-cols-2 gap-3 mt-2">
              <Card><CardContent className="py-4 text-center">
                <div className="text-3xl font-black text-emerald-700">{data.total_views}</div>
                <div className="text-xs text-slate-500 mt-1 uppercase">Total de views</div>
              </CardContent></Card>
              <Card><CardContent className="py-4 text-center">
                <div className="text-3xl font-black text-emerald-700">{data.unique_tenants}</div>
                <div className="text-xs text-slate-500 mt-1 uppercase">Clientes únicos</div>
              </CardContent></Card>
            </div>
            <div className="mt-4">
              <div className="text-xs font-semibold text-slate-600 uppercase mb-2">Top clientes por leitura</div>
              <Table>
                <TableHeader><TableRow>
                  <TableHead className="text-[10px]">Cliente</TableHead>
                  <TableHead className="text-[10px]">Plano</TableHead>
                  <TableHead className="text-[10px] text-right">Views</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {(data.per_tenant || []).map((r) => (
                    <TableRow key={r.tenant_id}>
                      <TableCell className="text-xs">{r.tenant_name}</TableCell>
                      <TableCell className="text-xs text-slate-500">{r.plan || '—'}</TableCell>
                      <TableCell className="text-xs font-bold text-right">{r.views}</TableCell>
                    </TableRow>
                  ))}
                  {(!data.per_tenant || data.per_tenant.length === 0) && (
                    <TableRow><TableCell colSpan={3} className="text-center text-xs text-slate-400 py-6">Nenhuma leitura ainda</TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

// ------- Main page -------
export const PlatformInsightsPage = () => {
  const navigate = useNavigate();
  const [insights, setInsights] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState('content');
  const [analyticsInsight, setAnalyticsInsight] = useState(null);

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [i, t] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/superadmin/insights`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/platform/tenants`, { withCredentials: true }),
      ]);
      setInsights(i.data || []);
      setTenants(t.data || []);
    } catch (_) { toast.error('Falha ao carregar dados'); }
    finally { setLoading(false); }
  };

  const openCreate = () => {
    setForm(EMPTY_FORM);
    setEditingId(null);
    setTab('content');
    setDialogOpen(true);
  };
  const openEdit = (ins) => {
    setForm({
      title: ins.title || '',
      subtitle: ins.subtitle || '',
      cover_image_url: ins.cover_image_url || '',
      body_html: ins.body_html || '',
      videos: ins.videos || [],
      tags: ins.tags || [],
      all_tenants: !!ins.all_tenants,
      target_tenants: ins.target_tenants || [],
      target_plans: ins.target_plans || [],
      target_status: ins.target_status || [],
      is_published: !!ins.is_published,
    });
    setEditingId(ins.id);
    setTab('content');
    setDialogOpen(true);
  };

  const saveInsight = async (alsoPublish = false) => {
    if (!form.title.trim()) { toast.error('Título é obrigatório'); return; }
    if (!form.body_html || form.body_html === '<p></p>') { toast.error('Corpo do artigo é obrigatório'); return; }
    setSaving(true);
    try {
      const payload = { ...form, is_published: alsoPublish ? true : form.is_published };
      if (editingId) {
        await axios.patch(`${BACKEND_URL}/api/superadmin/insights/${editingId}`, payload, { withCredentials: true });
        toast.success(alsoPublish && !form.is_published ? 'Publicado!' : 'Alterações salvas!');
      } else {
        await axios.post(`${BACKEND_URL}/api/superadmin/insights`, payload, { withCredentials: true });
        toast.success(alsoPublish ? 'Artigo criado e publicado!' : 'Rascunho salvo!');
      }
      setDialogOpen(false);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Falha ao salvar');
    } finally { setSaving(false); }
  };

  const deleteInsight = async (id) => {
    try {
      await axios.delete(`${BACKEND_URL}/api/superadmin/insights/${id}`, { withCredentials: true });
      toast.success('Artigo removido');
      fetchAll();
    } catch (_) { toast.error('Falha ao remover'); }
  };

  const summary = useMemo(() => ({
    total: insights.length,
    published: insights.filter((i) => i.is_published).length,
    drafts: insights.filter((i) => !i.is_published).length,
    views: insights.reduce((s, i) => s + (i.view_count || 0), 0),
  }), [insights]);

  const targetLabel = (ins) => {
    if (ins.all_tenants) return 'Todos';
    const chunks = [];
    if (ins.target_tenants?.length) chunks.push(`${ins.target_tenants.length} cliente(s)`);
    if (ins.target_plans?.length) chunks.push(`planos: ${ins.target_plans.join(',')}`);
    if (ins.target_status?.length) chunks.push(`status: ${ins.target_status.join(',')}`);
    return chunks.join(' + ') || '—';
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/platform')} className="text-slate-500 hover:text-slate-900" data-testid="insights-back-btn">
              <ArrowLeft className="h-5 w-5" />
            </button>
            <div>
              <h1 className="font-outfit text-xl font-black tracking-tight text-slate-900 flex items-center gap-2">
                <Sparkles className="h-5 w-5 text-emerald-600" />
                Biblioteca de Insights
              </h1>
              <p className="text-xs text-slate-500 -mt-0.5">Curadoria de conteúdo distribuído para os clientes</p>
            </div>
          </div>
          <Button onClick={openCreate} className="bg-emerald-600 hover:bg-emerald-700 gap-1.5" data-testid="insights-create-btn">
            <Plus className="h-4 w-4" /> Novo artigo
          </Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-6 space-y-4">
        <div className="grid grid-cols-4 gap-3">
          {[
            { label: 'Total', value: summary.total },
            { label: 'Publicados', value: summary.published },
            { label: 'Rascunhos', value: summary.drafts },
            { label: 'Total de views', value: summary.views },
          ].map((k) => (
            <Card key={k.label} className="border-slate-200">
              <CardContent className="py-3 text-center">
                <div className="text-2xl font-black text-slate-900" data-testid={`insights-summary-${k.label}`}>{k.value}</div>
                <div className="text-[10px] uppercase tracking-wider text-slate-500 mt-0.5">{k.label}</div>
              </CardContent>
            </Card>
          ))}
        </div>

        <Card className="border-slate-200">
          <CardContent className="p-0">
            <Table data-testid="insights-table">
              <TableHeader>
                <TableRow className="bg-slate-50 hover:bg-slate-50">
                  <TableHead className="text-[10px] uppercase tracking-wider font-bold text-slate-600">Artigo</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-wider font-bold text-slate-600">Destino</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-wider font-bold text-slate-600">Tags</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-wider font-bold text-slate-600">Status</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-wider font-bold text-slate-600 text-right">Views</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-wider font-bold text-slate-600 text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading && <TableRow><TableCell colSpan={6} className="text-center text-sm text-slate-400 py-8">Carregando…</TableCell></TableRow>}
                {!loading && insights.length === 0 && (
                  <TableRow><TableCell colSpan={6} className="text-center text-sm text-slate-500 py-10">
                    Nenhum artigo ainda. <button onClick={openCreate} className="text-emerald-700 font-semibold hover:underline">Criar o primeiro</button>.
                  </TableCell></TableRow>
                )}
                {insights.map((ins) => (
                  <TableRow key={ins.id} data-testid={`insight-row-${ins.id}`}>
                    <TableCell className="max-w-md">
                      <div className="font-semibold text-sm text-slate-900 truncate">{ins.title}</div>
                      {ins.subtitle && <div className="text-xs text-slate-500 truncate">{ins.subtitle}</div>}
                    </TableCell>
                    <TableCell className="text-xs text-slate-600">{targetLabel(ins)}</TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1 max-w-[180px]">
                        {(ins.tags || []).slice(0, 3).map((t) => (
                          <Badge key={t} variant="outline" className="text-[9px]">{t}</Badge>
                        ))}
                        {(ins.tags || []).length > 3 && <span className="text-[10px] text-slate-400">+{ins.tags.length - 3}</span>}
                      </div>
                    </TableCell>
                    <TableCell>
                      {ins.is_published ? (
                        <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200 text-[10px]">Publicado</Badge>
                      ) : (
                        <Badge variant="outline" className="text-[10px]">Rascunho</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right text-xs font-bold" data-testid={`insight-views-${ins.id}`}>
                      {ins.view_count || 0}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setAnalyticsInsight(ins)} title="Analytics" data-testid={`insight-analytics-${ins.id}`}>
                          <BarChart3 className="h-3.5 w-3.5" />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEdit(ins)} data-testid={`insight-edit-${ins.id}`}>
                          <Sparkles className="h-3.5 w-3.5" />
                        </Button>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-7 w-7 text-rose-600 hover:bg-rose-50">
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Remover este artigo?</AlertDialogTitle>
                              <AlertDialogDescription>Esta ação não pode ser desfeita. Notificações e analytics também serão apagados.</AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancelar</AlertDialogCancel>
                              <AlertDialogAction onClick={() => deleteInsight(ins.id)} className="bg-rose-600 hover:bg-rose-700">Remover</AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </main>

      {/* Editor Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="insight-editor-dialog">
          <DialogHeader>
            <DialogTitle>{editingId ? 'Editar artigo' : 'Novo artigo'}</DialogTitle>
            <DialogDescription>Rich text com suporte a vídeos, imagens e tags. Escolha a audiência antes de publicar.</DialogDescription>
          </DialogHeader>

          <Tabs value={tab} onValueChange={setTab} className="mt-2">
            <TabsList className="grid grid-cols-3">
              <TabsTrigger value="content">Conteúdo</TabsTrigger>
              <TabsTrigger value="media">Mídia & Tags</TabsTrigger>
              <TabsTrigger value="audience">Audiência</TabsTrigger>
            </TabsList>

            <TabsContent value="content" className="space-y-4 mt-4">
              <div>
                <Label className="text-sm font-semibold">Título *</Label>
                <Input
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value.slice(0, 140) })}
                  placeholder="Ex.: Como planejar sua campanha em 3 etapas"
                  className="mt-1.5"
                  data-testid="insight-title-input"
                />
              </div>
              <div>
                <Label className="text-sm font-semibold">Subtítulo (opcional)</Label>
                <Textarea
                  value={form.subtitle}
                  onChange={(e) => setForm({ ...form, subtitle: e.target.value.slice(0, 240) })}
                  rows={2}
                  placeholder="Uma frase que resume o artigo…"
                  className="mt-1.5 resize-none"
                />
              </div>
              <div>
                <Label className="text-sm font-semibold">Corpo do artigo *</Label>
                <div className="mt-1.5">
                  <TipTapEditor
                    value={form.body_html}
                    onChange={(html) => setForm({ ...form, body_html: html })}
                    onImageUpload={async ({ file }) => cloudinaryUpload(file, 'meadd/insights/inline', 'image')}
                    minHeight={280}
                  />
                </div>
              </div>
            </TabsContent>

            <TabsContent value="media" className="space-y-4 mt-4">
              <CoverUpload
                value={form.cover_image_url}
                onChange={(url) => setForm({ ...form, cover_image_url: url })}
              />
              <VideosBlock videos={form.videos} onChange={(v) => setForm({ ...form, videos: v })} />
              <TagsBlock tags={form.tags} onChange={(t) => setForm({ ...form, tags: t })} />
            </TabsContent>

            <TabsContent value="audience" className="mt-4">
              <TargetingBlock form={form} setForm={setForm} allTenants={tenants} />
            </TabsContent>
          </Tabs>

          <DialogFooter className="flex-row justify-between border-t border-slate-100 pt-4 mt-4">
            <Button variant="ghost" onClick={() => setDialogOpen(false)} disabled={saving}>Cancelar</Button>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => saveInsight(false)} disabled={saving} data-testid="insight-save-draft-btn">
                {saving ? 'Salvando…' : 'Salvar rascunho'}
              </Button>
              <Button onClick={() => saveInsight(true)} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700 gap-1.5" data-testid="insight-publish-btn">
                <Check className="h-4 w-4" />{saving ? 'Publicando…' : (form.is_published ? 'Salvar' : 'Publicar')}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AnalyticsDialog
        insight={analyticsInsight}
        open={!!analyticsInsight}
        onOpenChange={(v) => !v && setAnalyticsInsight(null)}
      />
    </div>
  );
};

export default PlatformInsightsPage;
